import { Handle, Position } from '@xyflow/react'
import { nodeControlClass } from '../../lib/nodeControlClass.js'
import { isFired } from './useFiredFlash'
import { selectedBorder } from './selectedBorder.js'
import {
  normalizeOptions, canMove, maxFor, setProbability, setLocked,
  addOption, removeOption, MIN_OPTIONS,
} from '../../lib/probabilitySelect.js'

export const PROBABILITY_SELECT_ACCENT = '#d0a040'
const ACCENT = PROBABILITY_SELECT_ACCENT
const PIN = '#e07040'   // animation pin colour, as Select's anim mode

/**
 * Probability Select — plays ONE of its animation inputs, picked at random by
 * weight. Each input has a probability (the row's slider / number) and a lock;
 * the probabilities always total 1, and moving one rebalances the unlocked
 * others (probabilitySelect.js has the rules). The pick is sticky until the
 * state it sits in is entered again, exactly like Random: GraphEvaluator keeps
 * it in the same sticky store and clearStickyRandomsInNodes re-rolls it.
 *
 * data.options — [{ p, locked }], at least two.
 */
export function ProbabilitySelectNode({ data, selected }) {
  const fired = isFired(data)
  const opts = normalizeOptions(data.options)
  const write = (next) => data.onChange?.('options', next)
  const pct = (p) => Math.round(p * 100) / 100

  return (
    <div style={{
      background: selected ? '#1e1808' : '#141008',
      border: `2px solid ${fired ? '#ffcc44' : selected ? selectedBorder('#605018') : '#605018'}`,
      borderRadius: 6,
      minWidth: 280,
      fontFamily: 'var(--m-font)',
      fontSize: 12,
      boxShadow: fired ? '0 0 14px rgba(255,200,40,0.6)' : 'none',
    }}>
      {/* ── Header ── */}
      <div style={{
        background: '#2a2008', padding: '7px 14px 7px 20px', position: 'relative',
        display: 'flex', alignItems: 'center', gap: 8,
        borderBottom: '1px solid #3a2c10', borderRadius: '4px 4px 0 0',
      }}>
        <span style={{ color: ACCENT, fontSize: 12, fontWeight: 'bold' }}>⚄</span>
        <span style={{ color: ACCENT, fontWeight: 'bold', fontSize: 11, letterSpacing: 0.5 }}>Probability Select</span>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6, position: 'relative' }}>
          <span style={{ fontSize: 10 }}>Return Value</span>
          <Handle type="source" position={Position.Right} id="anim"
            style={{ background: PIN, width: 10, height: 10, borderRadius: '50%' }} />
        </div>
      </div>

      {/* ── Options ── */}
      {opts.map((o, i) => {
        const movable = canMove(opts, i)
        const max = maxFor(opts, i)
        return (
          <div key={i} data-testid={`psel-row-${i}`} style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '6px 10px 6px 20px', borderBottom: '1px solid #1e1808', position: 'relative',
          }}>
            <Handle type="target" position={Position.Left} id={`option-${i}`}
              style={{ background: PIN, width: 10, height: 10, borderRadius: '50%', border: `2px solid ${PIN}55` }} />
            <span style={{ fontSize: 10, minWidth: 48 }}>Option {i}</span>
            <input
              type="range" min={0} max={1} step={0.01}
              className={nodeControlClass({ extra: 'nowheel' })}
              value={o.p}
              disabled={!movable}
              title={movable ? `0 – ${pct(max)}` : (o.locked ? 'Locked' : 'Every other option is locked')}
              onChange={e => write(setProbability(opts, i, parseFloat(e.target.value)))}
              style={{ flex: 1, minWidth: 70, accentColor: ACCENT, opacity: movable ? 1 : 0.4 }}
            />
            <input
              type="number" min={0} max={pct(max)} step={0.01}
              className={nodeControlClass()}
              value={pct(o.p)}
              disabled={!movable}
              data-testid={`psel-p-${i}`}
              onChange={e => {
                const v = parseFloat(e.target.value)
                if (Number.isFinite(v)) write(setProbability(opts, i, v))
              }}
              style={{
                width: 44, background: '#0a0806', border: '1px solid #2a2008',
                padding: '2px 4px', borderRadius: 3, fontSize: 10,
                fontFamily: 'var(--m-mono)', outline: 'none', textAlign: 'right',
                opacity: movable ? 1 : 0.5,
              }}
            />
            <button
              className={nodeControlClass()}
              data-testid={`psel-lock-${i}`}
              title={o.locked ? 'Unlock: let this probability change with the others' : 'Lock: keep this probability fixed'}
              onClick={() => write(setLocked(opts, i, !o.locked))}
              style={{
                background: o.locked ? '#3a2c10' : 'none', border: '1px solid #3a2c10',
                borderRadius: 3, cursor: 'pointer', fontSize: 10, padding: '1px 4px', lineHeight: 1.2,
              }}
            >{o.locked ? '🔒' : '🔓'}</button>
            {opts.length > MIN_OPTIONS && (
              <button
                className={nodeControlClass()}
                title="Remove this input (its share goes to the unlocked others)"
                onClick={() => write(removeOption(opts, i))}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, padding: '0 2px', lineHeight: 1 }}
              >×</button>
            )}
          </div>
        )
      })}

      {/* ── Add pin ── */}
      <div style={{ padding: '4px 14px', display: 'flex', justifyContent: 'flex-end' }}>
        <button
          className={nodeControlClass()}
          data-testid="psel-add"
          title="Add an input at 0 — nothing else changes"
          onClick={() => write(addOption(opts))}
          style={{ background: 'none', border: 'none', fontSize: 10, fontFamily: 'var(--m-font)', cursor: 'pointer' }}
        >Add pin ⊕</button>
      </div>
    </div>
  )
}
